import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../ui/button';
import { Upload, Euro, X, FileText, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n || 0);

/**
 * Rechnungen-Auswertungs-Sektion (25.08.2026).
 * - Upload-Button (Drag&Drop, auch aus Thunderbird) für PDF-Rechnungen.
 * - Nur Metadaten: Lieferant, Nummer, Datum, Endsumme.
 * - Aggregation: Gesamtsumme + Top-Lieferanten. Zeitraum wählbar
 *   (Aktueller Monat / Quartal / Jahr).
 */
export default function CompanyInvoicesSection() {
  const [scope, setScope] = useState('year');   // month | quarter | year
  const now = new Date();
  const [year] = useState(now.getFullYear());
  const [month] = useState(now.getMonth() + 1);
  const [quarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [analytics, setAnalytics] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ scope, year: String(year) });
    if (scope === 'month') params.set('month', String(month));
    if (scope === 'quarter') params.set('quarter', String(quarter));
    const [a, l] = await Promise.all([
      authFetch(`/api/company/invoices/analytics?${params.toString()}`),
      authFetch('/api/company/invoices?limit=30'),
    ]);
    if (a.ok) setAnalytics(await a.json());
    if (l.ok) setRows(await l.json());
    setLoading(false);
  }, [scope, year, month, quarter]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('Rechnung wirklich löschen?')) return;
    const r = await authFetch(`/api/company/invoices/${id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Gelöscht'); load(); }
    else toast.error('Löschen fehlgeschlagen');
  };

  return (
    <div className="mt-6 border-t border-gray-100 pt-5" data-testid="company-invoices-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-[#32373c]">
          Eingangsrechnungen (Auswertung)
        </h3>
        <div className="flex items-center gap-1.5">
          <select value={scope} onChange={(e) => setScope(e.target.value)}
                  className="h-8 border border-gray-200 rounded px-2 text-xs bg-white"
                  data-testid="ci-scope">
            <option value="month">Aktueller Monat</option>
            <option value="quarter">Aktuelles Quartal</option>
            <option value="year">Aktuelles Jahr</option>
          </select>
          <Button onClick={() => setShowUpload(true)}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-8 text-xs"
                  data-testid="btn-ci-upload">
            <Upload className="w-3.5 h-3.5 mr-1" /> Rechnung hochladen
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-gray-400 py-4 text-center">Lade Auswertung …</div>
      ) : analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="border border-gray-100 rounded-lg p-3 bg-emerald-50/40">
            <div className="text-[10px] uppercase text-gray-400">Gesamtsumme</div>
            <div className="text-xl font-bold text-[#32373c]" data-testid="ci-total">
              {fmtEur(analytics.grandTotal)}
            </div>
            <div className="text-[10px] text-gray-400 mt-0.5">
              {analytics.invoiceCount} Rechnung(en) · {analytics.from} – {analytics.to}
            </div>
          </div>
          <div className="lg:col-span-2 border border-gray-100 rounded-lg p-3 bg-emerald-50/40" data-testid="ci-by-supplier">
            <div className="text-xs font-semibold text-[#32373c] mb-2">Top-Lieferanten</div>
            {analytics.bySupplier.length === 0 ? (
              <div className="text-xs text-gray-400 italic">Noch keine Rechnungen im Zeitraum.</div>
            ) : (
              <div className="space-y-1">
                {analytics.bySupplier.slice(0, 6).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 border border-emerald-100">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="text-gray-700 truncate">{s.supplier}</span>
                      <span className="text-[10px] text-gray-400 shrink-0">· {s.count}</span>
                    </span>
                    <span className="text-[#32373c] font-medium shrink-0 tabular-nums">{fmtEur(s.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-3">
          <details>
            <summary className="text-xs text-gray-500 cursor-pointer hover:text-[#9ec043]">
              Letzte {rows.length} Rechnungen anzeigen
            </summary>
            <div className="mt-2 space-y-1">
              {rows.map(r => (
                <div key={r.id} className="flex items-center justify-between text-xs bg-white border border-gray-100 rounded px-2 py-1">
                  <span className="flex items-center gap-2 min-w-0">
                    <FileText className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span className="text-gray-700 truncate">{r.supplier || '—'}</span>
                    <span className="text-[10px] text-gray-400 shrink-0">
                      · Nr. {r.invoiceNumber || '—'} · {r.invoiceDate || '—'}
                    </span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="tabular-nums font-medium">{fmtEur(r.grandTotalEur)}</span>
                    <button onClick={() => del(r.id)} className="text-gray-400 hover:text-red-500" title="Löschen">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {showUpload && (
        <CompanyInvoiceUploadDialog
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); load(); }}
        />
      )}
    </div>
  );
}

function CompanyInvoiceUploadDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const isPdf = (f) => f?.type === 'application/pdf' || /\.pdf$/i.test(f?.name || '');

  const upload = async (force = false) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    const url = force ? '/api/company/invoices?force=true' : '/api/company/invoices';
    const r = await authFetch(url, { method: 'POST', body: fd });
    setUploading(false);
    if (r.status === 409) {
      const d = await r.json();
      const detail = d.detail || d;
      if (window.confirm(`${detail.message}\n\nTrotzdem erfassen?`)) return upload(true);
      return;
    }
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail?.message || d.detail || 'Upload fehlgeschlagen');
      return;
    }
    const doc = await r.json();
    toast.success(`Erfasst: ${doc.supplier || '—'} · ${fmtEur(doc.grandTotalEur)}`);
    onDone?.();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="ci-upload-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#32373c]">Rechnung hochladen</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="btn-ci-upload-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div
            onDrop={(e) => { e.preventDefault(); setDragActive(false);
              const files = Array.from(e.dataTransfer.files);
              const p = files.find(isPdf);
              if (p) setFile(p); else toast.error('Nur PDF unterstützt'); }}
            onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
            onClick={() => document.getElementById('ci-file-input').click()}
            className={`rounded border-2 border-dashed p-8 text-center text-sm cursor-pointer transition-colors ${
              dragActive ? 'border-[#9ec043] bg-[#f8faf2]' : 'border-gray-200 bg-gray-50 hover:border-[#9ec043]'
            }`}
            data-testid="ci-dropzone">
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <div className="text-[#32373c] font-medium">PDF hier ablegen oder auswählen</div>
            <div className="text-xs text-gray-400 mt-1">Auch aus Thunderbird gedraggt (max. 25 MB)</div>
            <input id="ci-file-input" type="file" accept="application/pdf" className="hidden"
                   onChange={(e) => { const f = e.target.files?.[0]; if (f && isPdf(f)) setFile(f); }} />
          </div>
          {file && (
            <div className="flex items-center justify-between rounded border border-gray-200 bg-white p-3 text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-[#9ec043] shrink-0" />
                <span className="truncate">{file.name}</span>
                <span className="text-gray-400 shrink-0">({(file.size / 1024).toFixed(0)} KB)</span>
              </div>
              <Button onClick={() => upload(false)} disabled={uploading}
                      className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-8"
                      data-testid="btn-ci-do-upload">
                {uploading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Analyse …</> :
                  <><Euro className="w-3.5 h-3.5 mr-1" /> Erfassen</>}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
